import React from "react";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { shallow } from "zustand/shallow";

import { useServices, useSnackbar } from "contexts";
import { getFeedbackAndAnswer } from "services";
import { decodeHtmlEntities } from "utils";
import useStore from "components/PerformanceReview/store";
import { templateTypes } from "components/PerformanceReview/constants";

import ResponseHighlight from "./ResponseHighlight";

/**
 * The question + your response + feedback block — mobile port of desktop's
 * `Question` + `PersonalizedFeedback`.
 */
export default function QuestionCard() {
  const question = useStore((s) => s.questions[s.currentQuestion], shallow);

  const isError = question?.isError;
  const skipped = isError && question?.statusCode === 301;

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h6-semiBold" color="primary.clr-300">
          Question
        </Typography>
        {question?.type && (
          <Chip
            label={`${question.type} question`}
            size="small"
            sx={{
              height: 24,
              bgcolor: "primary.clr-50",
              color: "primary.clr-300",
              border: "1px solid",
              borderColor: "primary.clr-100",
              textTransform: "capitalize",
              "& .MuiChip-label": { px: 2, fontSize: 12, fontWeight: 600 },
            }}
          />
        )}
      </Box>

      <Typography
        variant="body01-bold"
        color="neutral.clr-900"
        dangerouslySetInnerHTML={{
          __html: decodeHtmlEntities(question?.question || ""),
        }}
      />

      {isError ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "warning.clr-100",
            borderRadius: 2,
            p: 3,
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "warning.clr-500", fontSize: 18 }} />
          <Typography variant="body01-bold" color="warning.clr-500">
            {skipped
              ? "You skipped this question!"
              : "It seems your answer was not recorded properly!"}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Your Response */}
          <Box
            sx={{
              bgcolor: "primary.clr-50",
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography variant="body01-regular" color="neutral.clr-900">
              <Box component="span" sx={{ fontWeight: 700, color: "primary.clr-300" }}>
                Your Response:{" "}
              </Box>
              <ResponseHighlight question={question} />
            </Typography>
          </Box>

          <PersonalizedFeedback />
        </>
      )}
    </Box>
  );
}

function PersonalizedFeedback() {
  const service = useServices();
  const snackbar = useSnackbar();

  const currentIndex = useStore((s) => s.currentQuestion, shallow);
  const questions = useStore((s) => s.questions, shallow);
  const setQuestions = useStore((s) => s.setQuestions, shallow);
  const template = useStore((s) => s.template, shallow);

  const question = questions[currentIndex];
  const [loading, setLoading] = React.useState(false);

  const feedback = question?.content?.feedback || "";
  const improved = question?.personalizedAnswer || "";

  React.useEffect(() => {
    const needsFetch =
      (question?.type === "technical" && !question?.content?.feedback) ||
      (question?.type === "situational" &&
        !(question?.content?.feedback || question?.personalizedAnswer));

    if (!needsFetch || !question?.transcript?.length || !question?.answerId) return;

    let active = true;
    (async () => {
      try {
        setLoading(true);
        const result = await service.callService(
          getFeedbackAndAnswer,
          question.answerId,
          template?.domain,
          template?.pathway ? true : false,
          null
        );
        if (result && active) {
          const updated = [...questions];
          updated[currentIndex].personalizedAnswer = result.personalizedAnswer;
          updated[currentIndex].content.feedback = result.personalizedFeedback;
          updated[currentIndex].personalizedTips = result.personalizedTips;
          setQuestions(updated);
        }
      } catch (error) {
        console.error(error);
        snackbar.error("Oo oh! unable to generate feedback, try again");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, question?.answerId]);

  const isPracticeOrAssignment =
    template.type === templateTypes.PRACTICE ||
    template.type === templateTypes.ASSIGNMENT;

  if (loading) {
    return (
      <Box>
        <Skeleton width="50%" height={28} />
        <Skeleton variant="rounded" height={90} sx={{ mt: 1 }} />
      </Box>
    );
  }

  if (!isPracticeOrAssignment || !feedback) return null;

  return (
    <>
      <Box>
        <Typography variant="h6-semiBold" color="neutral.clr-700">
          Feedback on your response
        </Typography>
        <Typography variant="body01-regular" color="neutral.clr-900" sx={{ mt: 1, display: "block" }}>
          {feedback}
        </Typography>
      </Box>

      {improved && (
        <Box>
          <Typography variant="h6-semiBold" color="neutral.clr-700">
            Your Improved Response
          </Typography>
          <Typography variant="body01-regular" color="neutral.clr-900" sx={{ mt: 1, display: "block" }}>
            {improved}
          </Typography>
        </Box>
      )}
    </>
  );
}
